import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, User, ArrowLeft } from 'lucide-react';
import { allTraitKeys } from '@/lib/breedingValueTraits';
import Map from '@/components/Map';
import BreedingValuesTable from '@/components/BreedingValuesTable';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { MessageBreederButton } from '@/components/messaging/MessageBreederButton';
import { useListing } from '@/hooks/useListing';
import { useAuth } from '@/contexts/AuthContext';

const ListingDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  
  const { data: listing, isLoading, error } = useListing({ listingId: id || '' });

  // Check if we have an invalid listing ID
  if (!id) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-gray-600">Invalid listing ID.</p>
            <Link to="/marketplace" className="text-green-700 hover:text-green-800 mt-4 inline-block">
              Back to Marketplace
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-gray-600">Unable to load listing at the moment.</p>
            <p className="text-sm text-gray-500 mt-2">This might be because the listing ID is invalid or the listing doesn't exist.</p>
            <Link to="/marketplace" className="text-green-700 hover:text-green-800 mt-4 inline-block">
              Back to Marketplace
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Breeder location for the map - convert string id to number for Map component
  const breederLocation = [{
    id: parseInt(listing.breeder.id) || 1, // Convert UUID to number or use fallback
    name: listing.breeder.name,
    location: listing.breeder.location,
    lat: listing.breeder.lat,
    lng: listing.breeder.lng
  }];

  // Check if listing has any breeding value data
  const hasBreedingValues = allTraitKeys.some(key => {
    const val = listing[key as keyof typeof listing];
    return val !== null && val !== undefined && val !== '';
  });

  // Get first letter of business name for fallback avatar
  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <Link to="/marketplace" className="inline-flex items-center text-green-700 hover:text-green-800 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Marketplace
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2">
            {/* Main image */}
            <div className="mb-8">
              <div className="aspect-video rounded-lg overflow-hidden">
                <img 
                  src={listing.image} 
                  alt={listing.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Listing details */}
            <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
              <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4">
                <div className="mb-4 md:mb-0 md:pr-6 flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{listing.title}</h1>
                  <div className="flex items-center text-gray-600 mb-4">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span>{listing.location || 'Location not specified'}</span>
                  </div>
                </div>
                <div className="md:text-right md:ml-4">
                  {listing.price && (
                    <div className="text-2xl md:text-3xl font-bold">
                      <span className="text-green-700">{listing.price.toLocaleString()}</span>
                      <span className="text-sm text-gray-900 ml-1">{listing.price_currency || 'USD'}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <div className="text-sm text-gray-600">Type</div>
                  <div className="font-medium">{listing.type}</div>
                </div>
                {listing.age && (
                  <div>
                    <div className="text-sm text-gray-600">Age</div>
                    <div className="font-medium">{listing.age} years</div>
                  </div>
                )}
                {listing.weight && (
                  <div>
                    <div className="text-sm text-gray-600">Weight</div>
                    <div className="font-medium">{listing.weight} {listing.weight_unit || 'lbs'}</div>
                  </div>
                )}
                <div>
                  <div className="text-sm text-gray-600">Breed</div>
                  <div className="font-medium">{listing.breed}</div>
                </div>
              </div>

              {listing.description && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Description</h3>
                  <p className="text-gray-700 leading-relaxed">{listing.description}</p>
                </div>
              )}

              {/* Registration Number */}
              {listing.registration_number && (
                <div className="mt-6">
                  <div className="text-sm text-gray-600">Registration Number</div>
                  <div className="font-medium text-lg">{listing.registration_number}</div>
                </div>
              )}
            </div>

            {/* YouTube Video */}
            {listing.youtube_video_url && (() => {
              const extractYouTubeId = (url: string): string | null => {
                const patterns = [
                  /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
                ];
                for (const pattern of patterns) {
                  const match = url.match(pattern);
                  if (match) return match[1];
                }
                return null;
              };
              const videoId = extractYouTubeId(listing.youtube_video_url);
              if (!videoId) return null;
              return (
                <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
                  <h3 className="text-lg font-semibold mb-4">Video</h3>
                  <div className="aspect-video rounded-lg overflow-hidden">
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}`}
                      title={listing.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                </div>
              );
            })()}

            {/* Breeding Values */}
            {hasBreedingValues && (
              <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <h3 className="text-lg font-semibold">Breeding Values</h3>
                  <Badge variant="default">
                    {listing.evaluation_type || 'EPD'}
                  </Badge>
                </div>
                <BreedingValuesTable
                  title="Calving & Reproductive Traits"
                  fields={[
                    { key: 'calving_ease', abbr: 'CE' },
                    { key: 'birth_weight', abbr: 'BW' },
                    { key: 'gestation_length', abbr: 'GL' },
                    { key: 'scrotal_size', abbr: 'SC' },
                  ]}
                  listing={listing}
                  evaluationType={listing.evaluation_type || 'EPD'}
                />
                <BreedingValuesTable
                  title="Growth & Performance Traits"
                  fields={[
                    { key: 'day_200_weight', abbr: 'W200' },
                    { key: 'day_400_weight', abbr: 'W400' },
                    { key: 'day_600_weight', abbr: 'W600' },
                    { key: 'weaning_weight', abbr: 'WW' },
                    { key: 'yearling_weight', abbr: 'YW' },
                  ]}
                  listing={listing}
                  evaluationType={listing.evaluation_type || 'EPD'}
                />
                <BreedingValuesTable
                  title="Maternal Traits"
                  fields={[
                    { key: 'milk', abbr: 'MILK' },
                    { key: 'maternal_weaning_weight', abbr: 'MK' },
                    { key: 'total_maternal', abbr: 'TM' },
                  ]}
                  listing={listing}
                  evaluationType={listing.evaluation_type || 'EPD'}
                />
                <BreedingValuesTable
                  title="Carcass & Composition Traits"
                  fields={[
                    { key: 'marbling', abbr: 'MARB' },
                    { key: 'intramuscular_fat', abbr: 'IMF' },
                    { key: 'fat_thickness', abbr: 'FAT' },
                    { key: 'rump_fat', abbr: 'RF' },
                    { key: 'ribeye_area', abbr: 'REA' },
                    { key: 'eye_muscle_area', abbr: 'EMA' },
                    { key: 'retail_beef_yield', abbr: 'RBY' },
                    { key: 'carcass_weight', abbr: 'CW' },
                  ]}
                  listing={listing}
                  evaluationType={listing.evaluation_type || 'EPD'}
                />
              </div>
            )}

            {/* Map */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4">Listing Location</h3>
              <Map 
                breeders={breederLocation} 
                center={[listing.breeder.lng, listing.breeder.lat]} 
                zoom={8} 
                height="300px"
                listingTitle={listing.title}
                listingLocation={listing.location || 'Location not specified'}
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Contact breeder */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <MessageBreederButton
                  listingId={listing.id}
                  breederId={listing.breeder.id}
                  listingTitle={listing.title}
                  listingUrl={window.location.href}
                  className="w-full bg-green-700 hover:bg-green-800"
                />
                {!user && (
                  <p className="text-sm text-gray-600 text-center mt-4">
                    Sign in to contact the breeder directly
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Breeder info */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">About the Breeder</h3>
                <div className="flex items-center mb-4">
                  {listing.breeder.image ? (
                    <img 
                      src={listing.breeder.image} 
                      alt={listing.breeder.name}
                      className="w-12 h-12 rounded-full object-cover mr-3 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-green-700 flex items-center justify-center mr-3 flex-shrink-0">
                      <span className="text-white font-semibold text-lg">
                        {getInitials(listing.breeder.name)}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{listing.breeder.name}</div>
                    <div className="text-sm text-gray-600 truncate">{listing.breeder.location}</div>
                  </div>
                </div>

                <Link to={`/breeder/${listing.breeder.id}`}>
                  <Button variant="outline" className="w-full">
                    <User className="w-4 h-4 mr-2" />
                    View Profile
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ListingDetail;
